import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';
import {
  isNoShowRevertInGracePeriod,
  NO_SHOW_REVERT_GRACE_MINUTES,
} from '../../../util/dateUtils';
import LockIconWrapper from '../../components/LockIconWrapper';

function RevertNoShowButton({
  booking,
  onBookingUpdated,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { revertNoShow } = useInternalBooking();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const isInGracePeriod = useMemo(
    () => isNoShowRevertInGracePeriod(booking, nowTs),
    [booking, nowTs]
  );
  const hasRevertRight = authCtx.checkRightAny('internalBooking:special:revertNoShow');

  const canShow = useMemo(() => {
    if (!booking?._id || typeof onBookingUpdated !== 'function') return false;
    if (booking.status !== 'noShow') return false;
    if (booking.noShowInvoiceId || booking.noShowInvoiceSkippedAt || booking.noShowInvoicePaidAt) {
      return false;
    }
    return isInGracePeriod || hasRevertRight;
  }, [booking, hasRevertRight, isInGracePeriod, onBookingUpdated]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id) return;

    try {
      setIsSubmitting(true);
      const response = await revertNoShow(booking._id);
      if (response?.booking) {
        onBookingUpdated(booking._id, response.booking);
        toast.success('No-Show wurde zurückgesetzt');
        setOpen(false);
      }
    } catch (err) {
      if (err?.message !== 'INSUFFICIENT_RIGHTS') {
        toast.error('Fehler beim Zurücksetzen des No-Shows');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='warning'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={sx}
      >
        {isInGracePeriod ? (
          'No-Show zurücksetzen'
        ) : (
          <LockIconWrapper right='internalBooking:special:revertNoShow'>
            No-Show zurücksetzen
          </LockIconWrapper>
        )}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
        <DialogTitle>No-Show zurücksetzen?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            Die Buchung wird wieder als bestätigt geführt.
            {!isInGracePeriod
              ? ' Da der kurze Zeitraum abgelaufen ist, wird dafür das Spezialrecht benötigt.'
              : ` Dies ist innerhalb der ersten ${NO_SHOW_REVERT_GRACE_MINUTES} Minuten ohne Spezialrecht möglich.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='warning'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default RevertNoShowButton;
